import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from '../../../../../services/message/message.service';
import { RoutingTabComponent } from '../../../../../components/ui/tabs/routing-tab/routing-tab.component';
import { SearchInputComponent } from '../../../../../components/inputs/search-input/search-input.component';
import { EmptyStateComponent } from '../../../../../components/ui/empty-state/empty-state.component';
import { MessageDetailsRecipientList } from '../../../campaigns/types/messagesTypes';
import { ModalFormContainerComponent } from '../../../../../components/ui/modal-form-container/modal-form-container.component';
import { formatCount } from '../../../../../../utils/sms';

type TabTitle = 'Contact List' | 'Organisation List' | 'Tag List' | 'Segmented List';

interface RecipientTab {
  /** Which list the tab shows; the label in `title` is free to change. */
  key: TabTitle;
  title: string;
  count: number;
  link: string;
  queryParams?: Record<string, string>;
}

/**
 * Who a message was addressed to: the contacts, organizations, tags and
 * segments picked when it was composed, from /receipients.
 */
@Component({
  selector: 'recipient-list',
  standalone: true,
  imports: [RoutingTabComponent, SearchInputComponent, EmptyStateComponent, ModalFormContainerComponent],
  templateUrl: './recipient-list-temp.html',
  styleUrls: ['./recipient-list-style.css'],
})
export class RecipientList implements OnInit {
  @Input() data: any[] = []; // Pass data from parent
  @Input() add_recipient: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() selectedContactsChange = new EventEmitter<any[]>();
  @Input() selectedContacts: any[] = [];
  @Input() detail_data: any[] = []; // Pass data from parent
  @Input() messageUuid: string = '';
  @Input() recipientsData: MessageDetailsRecipientList | null = null;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);

  readonly formatCount = formatCount;

  totalRecipients: number = 0;
  contact_list: any[] = [];
  org_list: any[] = [];
  seg_list: any[] = [];
  tag_list: any[] = [];
  selectd_tab: TabTitle = 'Contact List';
  loading: boolean = false;
  fetchError: string = '';
  searchTerm: string = '';

  tabs: RecipientTab[] = [];

  closeModal(): void {
    this.close.emit();
  }

  get isContacts(): boolean {
    return this.selectd_tab === 'Contact List';
  }

  /** The current tab's rows, narrowed by the search. */
  get rows(): any[] {
    const list =
      this.selectd_tab === 'Organisation List'
        ? this.org_list
        : this.selectd_tab === 'Tag List'
        ? this.tag_list
        : this.selectd_tab === 'Segmented List'
        ? this.seg_list
        : this.contact_list;
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return list;
    return list.filter((row) =>
      [row?.fullName, row?.mobileNumber, row?.name, row?.description, row?.country]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }

  get emptyMessage(): string {
    switch (this.selectd_tab) {
      case 'Organisation List':
        return 'No organizations were picked for this message.';
      case 'Tag List':
        return 'No tags were picked for this message.';
      case 'Segmented List':
        return 'No segments were picked for this message.';
      default:
        return 'No contacts were picked one by one for this message.';
    }
  }

  get searchPlaceholder(): string {
    return this.isContacts ? 'Search by name or number' : 'Search by name';
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
  }

  ngOnInit() {
    const basePath = this.router.url.split('?')[0];
    // Labels match the details page's Audience panel. The ?recipientTab values stay as they were, for old links.
    this.tabs = [
      { key: 'Contact List', title: 'Contacts', count: 0, link: basePath, queryParams: {} },
      {
        key: 'Organisation List',
        title: 'Organizations',
        count: 0,
        link: basePath,
        queryParams: { recipientTab: 'organisations' },
      },
      { key: 'Tag List', title: 'Tags', count: 0, link: basePath, queryParams: { recipientTab: 'tags' } },
      { key: 'Segmented List', title: 'Segments', count: 0, link: basePath, queryParams: { recipientTab: 'segments' } },
    ];

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const tab = params['recipientTab'];
      if (!tab) this.selectd_tab = 'Contact List';
      else if (tab === 'organisations') this.selectd_tab = 'Organisation List';
      else if (tab === 'tags') this.selectd_tab = 'Tag List';
      else if (tab === 'segments') this.selectd_tab = 'Segmented List';
    });

    if (this.recipientsData) {
      this.applyRecipientsData(this.recipientsData);
    } else {
      this.loading = true;
      this.fetchError = '';
      this.messageService
        .fetchMessageRecipients(this.messageUuid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: MessageDetailsRecipientList) => {
            this.applyRecipientsData(data);
            this.loading = false;
          },
          error: () => {
            this.loading = false;
            this.fetchError = 'Recipients could not be loaded. Close this window and try again.';
          },
        });
    }
  }

  private applyRecipientsData(data: MessageDetailsRecipientList | null) {
    const list = (value: unknown): any[] => (Array.isArray(value) ? value : []);
    this.contact_list = list(data?.contacts);
    this.tag_list = list(data?.engagementRecipientTags);
    this.org_list = list(data?.engagementRecipientCompanies);
    this.seg_list = list(data?.engagementRecipientSegments);
    this.totalRecipients = Number(data?.totalReceipients) || 0;

    const counts: Record<TabTitle, number> = {
      'Contact List': this.contact_list.length,
      'Organisation List': this.org_list.length,
      'Tag List': this.tag_list.length,
      'Segmented List': this.seg_list.length,
    };
    // A new array, so the OnPush tab strip sees the counts.
    this.tabs = this.tabs.map((tab) => ({ ...tab, count: counts[tab.key] ?? 0 }));
  }
}
